import { useCallback, useEffect, useRef } from "react";

import { useBottomSheetNestingContext } from "./useBottomSheetNestingContext";

/** Default drag transition */
const DRAG_TRANSITION = "transform 380ms cubic-bezier(0.2, 0.9, 0.25, 1)";
/** iOS-style spring curve for nesting animations */
const NESTING_TRANSITION = "transform 500ms cubic-bezier(0.32, 0.72, 0, 1)";

/**
 * Builds a composed CSS transform string from nesting scale and drag offset.
 */
function buildTransform(scale: number, nestingY: number, dragY: number): string {
  const totalY = nestingY + dragY;
  if (scale === 1 && totalY === 0) {
    return "translateY(0px)";
  }

  if (scale === 1) {
    return `translateY(${totalY}px)`;
  }

  return `scale(${scale}) translateY(${totalY}px)`;
}

interface UseBottomSheetNestingParams {
  paperRef: React.RefObject<HTMLDivElement | null>;
  modalIsOpen: boolean;
}

/**
 * Connects a BottomSheet to the nesting stack automatically.
 *
 * When this sheet opens, it pushes itself onto the stack — the sheet below
 * scales down. During drag, the sheet below interpolates back in real-time.
 * No explicit `isNestingParent`/`isNestedChild` props needed.
 */
export function useBottomSheetNesting(params: UseBottomSheetNestingParams) {
  const { paperRef, modalIsOpen } = params;
  const nesting = useBottomSheetNestingContext();

  // Track current drag Y offset so useDrag can resume from mid-drag position
  const currentY = useRef(0);
  // Track nesting transform state (applied by the stack when a sheet above opens/drags)
  const nestingScale = useRef(1);
  const nestingTranslateY = useRef(0);

  // Unified transform writer — composes nesting scale+translate with drag offset
  const applyTransform = useCallback(
    (dragY: number, withTransition: boolean, transitionTiming?: string) => {
      const element = paperRef.current;
      if (!element) {
        return;
      }

      const timing = transitionTiming ?? DRAG_TRANSITION;
      element.style.transition = withTransition ? timing : "none";
      element.style.transform = buildTransform(
        nestingScale.current,
        nestingTranslateY.current,
        dragY
      );
    },
    [paperRef]
  );

  // Set drag offset — delegates to applyTransform with current nesting state
  const setY = useCallback(
    (y: number, withTransition = true) => {
      currentY.current = y;
      applyTransform(y, withTransition);
    },
    [applyTransform]
  );

  // Callback the stack uses to drive our nesting transform from above
  const applyNestingTransform = useCallback(
    (scale: number, translateY: number, withTransition: boolean) => {
      nestingScale.current = scale;
      nestingTranslateY.current = translateY;
      applyTransform(currentY.current, withTransition, NESTING_TRANSITION);
    },
    [applyTransform]
  );

  // Push onto / pop from the nesting stack when this sheet opens/closes
  useEffect(() => {
    if (!modalIsOpen) {
      return;
    }

    const pop = nesting.push({ applyNestingTransform });

    return () => {
      pop();
    };
  }, [modalIsOpen, nesting, applyNestingTransform]);

  return { currentY, setY, notifyDrag: nesting.notifyDrag, notifyRelease: nesting.notifyRelease };
}
